import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FaCheck, FaDownload, FaFileCsv, FaSearch, FaTimes, FaUpload } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api, { apiUpload } from '../../../helpers/apiRequester'
import styles from './index.module.scss'

/**
 * Admin > Omnibus comms upload.
 *
 * Same importer as scripts/omnibus/importClientComms.mjs, behind a button.
 * Flow: pick a conversations.csv → Preview (dry run, nothing written) →
 * Import. Both calls return the same summary shape plus a log, and the
 * unmatched contacts can be downloaded as CSV for follow-up.
 */

interface UnmatchedContact {
    contact_id: string
    first_name: string | null
    last_name: string | null
    email: string | null
    phone: string | null
}

interface UploadSummary {
    dry_run: boolean
    file_name: string
    started_at: string
    finished_at: string
    rows: number
    contacts: number
    messages_new: number
    messages_skipped_existing: number
    rows_invalid: number
    emails_missing_html: number
    date_range: { from: string; to: string } | null
    by_category: Record<string, number>
    contacts_matched: number
    contacts_unmatched: number
    omnibus_clients_linked: number
    match_methods: Record<string, number>
    unmatched_contacts: UnmatchedContact[]
    invalid_rows: Array<{ row: number; reason: string; message_id: string | null }>
    write_errors: string[]
    log: string[]
}

const EXPECTED_COLUMNS =
    'contact_id, first_name, last_name, phone, email, conversation_id, message_id, channel, direction, status, sent_at_raw, subject, email_from, email_to, message_text, html_file'

const fmt = (n: number | undefined) => (n ?? 0).toLocaleString('en-GB')

const downloadCsv = (fileName: string, header: string[], rows: Array<Array<string | null | undefined>>) => {
    const escape = (v: string | null | undefined) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const body = [header.map(escape).join(','), ...rows.map((r) => r.map(escape).join(','))].join('\n')
    const blob = new Blob([body], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.click()
    URL.revokeObjectURL(url)
}

interface OmnibusCommsUploadViewProps {
    /**
     * True when hosted inside another admin-gated page (the omnibus claims
     * page). Skips the standalone access check, which would otherwise bounce
     * the user away from the host page on a 403.
     */
    embedded?: boolean
}

export const OmnibusCommsUploadView = ({ embedded = false }: OmnibusCommsUploadViewProps) => {
    const navigate = useNavigate()
    const inputRef = useRef<HTMLInputElement>(null)
    const [file, setFile] = useState<File | null>(null)
    const [busy, setBusy] = useState<'preview' | 'import' | null>(null)
    const [result, setResult] = useState<UploadSummary | null>(null)
    const [history, setHistory] = useState<UploadSummary[]>([])

    // Bounce non-admins straight away rather than after they pick a file.
    useEffect(() => {
        if (embedded) return
        api('/users-page-auth').catch((err: any) => {
            if (err?.response?.status === 403) {
                Report.failure(
                    'Access Denied',
                    'Comms uploads are managed by Sentinel admins only.',
                    'Return to Dashboard',
                    () => navigate('/home'),
                )
            }
        })
    }, [embedded, navigate])

    const pickFile = (picked: File | null) => {
        setResult(null)
        if (picked && !/\.csv$/i.test(picked.name)) {
            Report.failure('Wrong file type', 'Only .csv conversation exports are accepted.', 'Okay')
            setFile(null)
            return
        }
        setFile(picked)
    }

    const run = useCallback(
        async (mode: 'preview' | 'import') => {
            if (!file) return
            setBusy(mode)
            try {
                const formData = new FormData()
                formData.append('file', file)
                formData.append('dry_run', mode === 'preview' ? 'true' : 'false')
                const response = await apiUpload('omnibus-client-comms-upload', formData)
                if (response.data?.success) {
                    const summary = response.data as UploadSummary
                    setResult(summary)
                    if (mode === 'import') setHistory((prev) => [summary, ...prev])
                } else {
                    Report.failure('Upload failed', response.data?.message || 'The export could not be processed.', 'Okay')
                }
            } catch (err: any) {
                if (err?.response?.status === 403) {
                    Report.failure('Access Denied', 'Comms uploads are managed by Sentinel admins only.', 'Return to Dashboard', () => navigate('/home'))
                } else {
                    Report.failure('Upload failed', err?.response?.data?.message || 'The export could not be processed.', 'Okay')
                }
            } finally {
                setBusy(null)
            }
        },
        [file, navigate],
    )

    const categories = result ? Object.entries(result.by_category).sort((a, b) => b[1] - a[1]) : []

    return (
        <div className={styles.wrap}>
            <section className={styles.card}>
                <div className={styles.cardHeader}>
                    <h3 className={styles.cardTitle}>1. Choose the export</h3>
                    <p className={styles.cardIntro}>
                        A <code>conversations.csv</code> from the comms provider, one row per message. Expected columns:{' '}
                        <code className={styles.columns}>{EXPECTED_COLUMNS}</code>. Email HTML must already be in
                        S3 under <code>communications/emails/</code>; rows whose file is missing are still recorded
                        and flagged.
                    </p>
                </div>
                <div className={styles.fileRow}>
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".csv,text/csv"
                        className={styles.fileInput}
                        onChange={(e) => pickFile(e.target.files?.[0] || null)}
                    />
                    <button type="button" className={styles.secondaryBtn} onClick={() => inputRef.current?.click()} disabled={busy !== null}>
                        <FaFileCsv /> {file ? 'Change file' : 'Choose CSV'}
                    </button>
                    <span className={styles.fileName}>
                        {file ? `${file.name} · ${(file.size / 1024 / 1024).toFixed(1)} MB` : 'No file selected'}
                    </span>
                    {file && (
                        <button
                            type="button"
                            className={styles.linkBtn}
                            onClick={() => {
                                pickFile(null)
                                if (inputRef.current) inputRef.current.value = ''
                            }}
                            disabled={busy !== null}
                        >
                            <FaTimes /> Clear
                        </button>
                    )}
                </div>
                <div className={styles.actions}>
                    <button type="button" className={styles.secondaryBtn} onClick={() => run('preview')} disabled={!file || busy !== null}>
                        <FaSearch /> {busy === 'preview' ? 'Previewing…' : 'Preview (nothing written)'}
                    </button>
                    <button
                        type="button"
                        className={styles.primaryBtn}
                        onClick={() => run('import')}
                        disabled={!file || busy !== null}
                        title={result && result.dry_run ? 'Write the previewed messages' : 'Import without previewing'}
                    >
                        <FaUpload /> {busy === 'import' ? 'Importing…' : 'Import'}
                    </button>
                    {busy && <span className={styles.busyNote}>Large exports take a minute or two — the S3 email index is checked first.</span>}
                </div>
            </section>

            {result && (
                <section className={styles.card}>
                    <div className={styles.cardHeader}>
                        <h3 className={styles.cardTitle}>
                            {result.dry_run ? 'Preview' : 'Import'} result · {result.file_name}
                        </h3>
                        <span className={result.dry_run ? styles.pillPreview : styles.pillDone}>
                            {result.dry_run ? 'Dry run — nothing written' : <><FaCheck /> Written</>}
                        </span>
                    </div>

                    <div className={styles.statGrid}>
                        <div className={styles.stat}><span className={styles.statLabel}>Rows in file</span><span className={styles.statValue}>{fmt(result.rows)}</span></div>
                        <div className={styles.stat}><span className={styles.statLabel}>{result.dry_run ? 'New messages' : 'Messages written'}</span><span className={styles.statValue}>{fmt(result.messages_new)}</span></div>
                        <div className={styles.stat}><span className={styles.statLabel}>Already stored (skipped)</span><span className={styles.statValue}>{fmt(result.messages_skipped_existing)}</span></div>
                        <div className={styles.stat}><span className={styles.statLabel}>Contacts in file</span><span className={styles.statValue}>{fmt(result.contacts)}</span></div>
                        <div className={styles.stat}><span className={styles.statLabel}>Matched to omnibus clients</span><span className={styles.statValue}>{fmt(result.contacts_matched)}</span></div>
                        <div className={styles.stat}><span className={styles.statLabel}>Unmatched contacts</span><span className={`${styles.statValue} ${result.contacts_unmatched ? styles.warn : ''}`}>{fmt(result.contacts_unmatched)}</span></div>
                        <div className={styles.stat}><span className={styles.statLabel}>Omnibus clients linked</span><span className={styles.statValue}>{fmt(result.omnibus_clients_linked)}</span></div>
                        <div className={styles.stat}><span className={styles.statLabel}>Emails missing HTML in S3</span><span className={`${styles.statValue} ${result.emails_missing_html ? styles.warn : ''}`}>{fmt(result.emails_missing_html)}</span></div>
                        <div className={styles.stat}><span className={styles.statLabel}>Invalid rows</span><span className={`${styles.statValue} ${result.rows_invalid ? styles.warn : ''}`}>{fmt(result.rows_invalid)}</span></div>
                    </div>

                    {result.date_range && (
                        <p className={styles.meta}>
                            New messages span <strong>{result.date_range.from}</strong> → <strong>{result.date_range.to}</strong>.
                        </p>
                    )}

                    {categories.length > 0 && (
                        <div className={styles.split}>
                            <div>
                                <h4 className={styles.subTitle}>New messages by category</h4>
                                <table className={styles.table}>
                                    <tbody>
                                        {categories.map(([category, n]) => (
                                            <tr key={category}><td>{category}</td><td className={styles.num}>{fmt(n)}</td></tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div>
                                <h4 className={styles.subTitle}>How contacts were matched</h4>
                                <table className={styles.table}>
                                    <tbody>
                                        {Object.entries(result.match_methods).map(([method, n]) => (
                                            <tr key={method}><td>{method.replace(/_/g, ' ')}</td><td className={styles.num}>{fmt(n)}</td></tr>
                                        ))}
                                        {Object.keys(result.match_methods).length === 0 && (
                                            <tr><td className={styles.empty} colSpan={2}>No contacts matched</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {result.write_errors.length > 0 && (
                        <div className={styles.errorBox}>
                            <strong>Some rows failed to write:</strong>
                            <ul>{result.write_errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                        </div>
                    )}

                    {result.unmatched_contacts.length > 0 && (
                        <div>
                            <div className={styles.rowBetween}>
                                <h4 className={styles.subTitle}>Unmatched contacts ({fmt(result.contacts_unmatched)})</h4>
                                <button
                                    type="button"
                                    className={styles.linkBtn}
                                    onClick={() =>
                                        downloadCsv(
                                            `comms-unmatched-${result.file_name.replace(/\.csv$/i, '')}.csv`,
                                            ['contact_id', 'first_name', 'last_name', 'email', 'phone'],
                                            result.unmatched_contacts.map((c) => [c.contact_id, c.first_name, c.last_name, c.email, c.phone]),
                                        )
                                    }
                                >
                                    <FaDownload /> Download CSV
                                </button>
                            </div>
                            <p className={styles.meta}>
                                These contacts have no omnibus client with the same email or phone. Their messages are
                                stored but will not show on any claim until a matching client is captured.
                            </p>
                            <div className={styles.scroll}>
                                <table className={styles.table}>
                                    <thead><tr><th>Contact</th><th>Name</th><th>Email</th><th>Phone</th></tr></thead>
                                    <tbody>
                                        {result.unmatched_contacts.slice(0, 200).map((c) => (
                                            <tr key={c.contact_id}>
                                                <td className={styles.mono}>{c.contact_id}</td>
                                                <td>{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</td>
                                                <td>{c.email || '—'}</td>
                                                <td>{c.phone || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {result.unmatched_contacts.length > 200 && (
                                    <p className={styles.meta}>Showing 200 of {fmt(result.unmatched_contacts.length)} — download the CSV for the full list.</p>
                                )}
                            </div>
                        </div>
                    )}

                    {result.invalid_rows.length > 0 && (
                        <div>
                            <h4 className={styles.subTitle}>Invalid rows (first {result.invalid_rows.length})</h4>
                            <table className={styles.table}>
                                <thead><tr><th>CSV row</th><th>Reason</th><th>message_id</th></tr></thead>
                                <tbody>
                                    {result.invalid_rows.map((r) => (
                                        <tr key={r.row}><td className={styles.num}>{r.row}</td><td>{r.reason}</td><td className={styles.mono}>{r.message_id || '—'}</td></tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <details className={styles.log}>
                        <summary>Log ({result.log.length} lines)</summary>
                        <pre>{result.log.join('\n')}</pre>
                    </details>
                </section>
            )}

            {history.length > 0 && (
                <section className={styles.card}>
                    <div className={styles.cardHeader}>
                        <h3 className={styles.cardTitle}>Imported this session</h3>
                    </div>
                    <table className={styles.table}>
                        <thead>
                            <tr><th>File</th><th>Finished</th><th>Written</th><th>Skipped</th><th>Matched</th><th>Unmatched</th></tr>
                        </thead>
                        <tbody>
                            {history.map((h, i) => (
                                <tr key={`${h.file_name}-${i}`}>
                                    <td>{h.file_name}</td>
                                    <td>{h.finished_at}</td>
                                    <td className={styles.num}>{fmt(h.messages_new)}</td>
                                    <td className={styles.num}>{fmt(h.messages_skipped_existing)}</td>
                                    <td className={styles.num}>{fmt(h.contacts_matched)}</td>
                                    <td className={styles.num}>{fmt(h.contacts_unmatched)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            )}

            <p className={styles.note}>
                Idempotent: a message_id that is already stored is never overwritten or duplicated, so you can re-upload
                an export that overlaps earlier ones. Messages recorded by other means are untouched by uploads.
            </p>
        </div>
    )
}
